/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { ItemFrame } from "../../shared/contracts";
import type { Ball } from "../items/ball/ball";
import type { Vertex3D } from "../math/vertex3d";
import type { Table } from "../table-model";
import type { EventSink } from "./event-proxy";
import { frameEquals } from "./item-state";
import type { IBallCreationPosition } from "./player-physics";
import { PlayerPhysics } from "./player-physics";
import type { Rng } from "./rng";

/**
 * The player runs a table. It sets up the items, owns the physics loop and
 * reports the changed item values after each step.
 *
 * Upstream the player also reads the keyboard, runs the script and draws the
 * DMD. Those parts are not here. The `Game` class reads the inputs.
 */
export class Player {
  private readonly table: Table;
  private readonly physics: PlayerPhysics;
  private readonly sink: EventSink;
  private readonly lastFrames = new Map<string, ItemFrame>();
  private isInitialized = false;

  get balls() {
    return this.physics.balls;
  }

  constructor(table: Table, rng: Rng, sink: EventSink) {
    this.table = table;
    this.sink = sink;
    this.physics = new PlayerPhysics(table, rng);
  }

  public init(): this {
    for (const playable of this.table.getPlayables()) {
      playable.setupPlayer(this, this.table);
    }
    this.physics.init();
    this.isInitialized = true;
    return this;
  }

  /** Runs one physics step of 1 ms. */
  public updatePhysics(): void {
    if (!this.isInitialized) {
      throw new Error("Player must be initialized before it runs physics.");
    }
    this.physics.step();
  }

  /**
   * Runs one animation cycle for the given time. Call it once per `step()` of the engine.
   *
   * @param timeMs Absolute game time
   */
  public updateAnimations(timeMs: number) {
    for (const animatable of this.table.getAnimatables()) {
      animatable.getAnimation().updateAnimation(timeMs, this.table);
    }
  }

  /**
   * Returns the item frames that changed since the last call.
   * The first call returns every item that can move.
   */
  public popItemFrames(): ItemFrame[] {
    const changed: ItemFrame[] = [];
    for (const source of this.table.getFrameSources()) {
      const frame = source.toFrame();
      if (!frame) {
        continue;
      }
      const last = this.lastFrames.get(frame.name);
      if (!last || !frameEquals(last, frame)) {
        this.lastFrames.set(frame.name, frame);
        changed.push(frame);
      }
    }
    return changed;
  }

  public createBall(
    ballCreator: IBallCreationPosition,
    radius = this.table.data.ballRadius,
    mass = this.table.data.ballMass,
    velocity?: Vertex3D,
  ): Ball {
    return this.physics.createBall(ballCreator, radius, mass, velocity);
  }

  public destroyBall(ball: Ball): void {
    this.physics.destroyBall(ball);
  }

  public getActiveBall(): Ball | undefined {
    return this.physics.activeBall;
  }

  public getGameTime(): number {
    return this.physics.timeMsec;
  }

  public getBalls(): Ball[] {
    return this.physics.balls;
  }

  public getPhysics(): PlayerPhysics {
    return this.physics;
  }

  public getEventSink(): EventSink {
    return this.sink;
  }

  public setGravity(slopeDeg: number, strength: number): void {
    this.physics.setGravity(slopeDeg, strength);
  }
}
